import { FC, memo, useCallback, useMemo } from 'react'
import { Control } from 'react-hook-form'
import {
  ColumnDef,
  OnChangeFn,
  Row,
  SortingState,
  TableOptions,
  getCoreRowModel,
} from '@tanstack/react-table'
import classNames from 'classnames'
import { ValueChangedEvent as TextBoxValueChangedEvent } from 'devextreme/ui/text_box'
import Lock from '~icons/material-symbols/lock'
import PublicIcon from '~icons/material-symbols/public'

import { Region, SettingKeyType } from '@crew/enums/app'
import { TaskTemplateScope } from '@crew/enums/domain'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting } from '@crew/states'

import { CrewTextBox } from 'components/devextreme/crewTextBox'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import {
  FormValues,
  TaskTemplateListItem,
} from '../../useTaskEntryByTemplateForm'

// renderとして使うのでmemo不可
const UpdateAt: FC<{ date: string }> = (props) => {
  const { t } = useTranslation()

  // ユーザー設定からデフォルトのユーザープロファイル地域を取得
  const defaultUserProfileRegion = useUserSetting(
    SettingKeyType.UserProfileRegion,
    Region.Japan.value
  )

  return (
    <>
      {props.date
        ? t('format.timestamp', {
            value: props.date,
            timeZone: defaultUserProfileRegion,
          })
        : undefined}
    </>
  )
}

type TaskTemplateListPanelProps = {
  control: Control<FormValues>
  templates: TaskTemplateListItem[]
  selectedTemplateId: string | null
  onSelectTemplate: (templateId: string) => void
  onSearchKeywordChange: (keyword: string) => void
  sort: string[]
  setSort: (sort: string[]) => void
}

// タスクテンプレート一覧パネル
export const TaskTemplateListPanel: FC<TaskTemplateListPanelProps> = memo(
  (props) => {
    const { t } = useTranslation()

    // テンプレート選択時の処理
    const handleSelectTemplate = useCallback(
      (row: Row<TaskTemplateListItem>) => {
        props.onSelectTemplate(row.original.id)
      },
      [props]
    )

    // 検索キーワード変更時の処理
    const handleSearchKeywordChanged = useCallback(
      (e: TextBoxValueChangedEvent) => {
        const value = (e.value ?? '') as string
        props.onSearchKeywordChange(value)
      },
      [props]
    )

    // ソート状態
    const sorting: SortingState = useMemo(() => {
      const sortArray = props.sort || []
      return sortArray.map((sort) => {
        const [id, direction] = sort.split('.')
        return {
          id,
          desc: direction === 'desc',
        }
      })
    }, [props.sort])

    // ソート変更時の処理
    const handleSortingChange: OnChangeFn<SortingState> = useCallback(
      (updaterOrValue) => {
        let values: SortingState
        if (updaterOrValue instanceof Function) {
          values = updaterOrValue(sorting)
        } else {
          values = updaterOrValue
        }

        const sortList = values.map((sort) => {
          return `${sort.id}.${sort.desc ? 'desc' : 'asc'}`
        })

        props.setSort(sortList)
      },
      [props, sorting]
    )

    // テンプレート一覧のカラム定義
    const columns = useMemo<ColumnDef<TaskTemplateListItem>[]>(
      () => [
        // 公開範囲
        {
          id: 'scope',
          accessorKey: 'scope',
          header: '',
          cell: ({ row }) => {
            const template = row.original
            const isPrivate = template.scope === TaskTemplateScope.Private
            return isPrivate ? (
              <Lock width={16} height={16} />
            ) : (
              <PublicIcon width={16} height={16} />
            )
          },
          size: 40,
          minSize: 40,
          meta: {
            align: 'center',
          },
        },
        // テンプレート名
        {
          id: 'name',
          accessorKey: 'name',
          header: t('label.taskTemplate.templateName'),
          cell: ({ row }) => {
            const template = row.original
            return <span className="truncate">{template.name}</span>
          },
          size: 200,
          minSize: 100,
          enableSorting: true,
        },
        // 更新者
        {
          id: 'updatedById',
          accessorKey: 'updatedBy',
          header: t('label.updatedBy'),
          cell: ({ row }) => (
            <div className="truncate">
              <CrewUserAvatar
                userId={row.original.updatedBy.id}
                displayName={row.original.updatedBy.displayName}
                cacheValue={row.original.updatedBy.id}
                size={CrewAvatarSize.xs}
                showLabel={true}
                disableClick={true}
              />
            </div>
          ),
          size: 140,
          minSize: 120,
          enableSorting: true,
        },
        // 更新日時
        {
          id: 'updatedAt',
          accessorKey: 'updatedAt',
          header: t('label.updateDatetime'),
          cell: ({ row }) => (
            <div className="truncate">
              <UpdateAt date={row.original.updatedAt} />
            </div>
          ),
          size: 160,
          minSize: 50,
          enableSorting: true,
        },
      ],
      [t]
    )

    // テーブルオプション
    const tableOptions: TableOptions<TaskTemplateListItem> = useMemo(
      () => ({
        data: props.templates,
        columns,
        columnResizeMode: 'onChange',
        getCoreRowModel: getCoreRowModel(),
        // ソートを有効化
        state: {
          sorting,
        },
        onSortingChange: handleSortingChange,
        manualSorting: true,
        enableMultiSort: true,
        maxMultiSortColCount: 2,
        meta: {
          headerRowHeight: 32,
          dataRowHeight: 32,
          getRowClassName: (row) => {
            const isSelected = row.original.id === props.selectedTemplateId
            return {
              group: classNames('cursor-pointer'),
              groupHover: classNames({
                'bg-crew-blue-1-light dark:bg-crew-blue-3-dark': isSelected,
              }),
            }
          },
        },
      }),
      [
        columns,
        handleSortingChange,
        props.selectedTemplateId,
        props.templates,
        sorting,
      ]
    )

    return (
      <div className="h-full w-full flex flex-col gap-y-1">
        {/* キーワード検索 */}
        <div>
          <CrewTextBox
            id="task-template-search"
            name="searchKeyword"
            mode="search"
            placeholder={t('label.search')}
            onValueChanged={handleSearchKeywordChanged}
          />
        </div>

        {/* タスクテンプレート一覧 */}
        {props.templates.length === 0 ? (
          // テンプレートが一件もない場合
          <div>{t('label.taskTemplate.noAvailableTemplates')}</div>
        ) : (
          // テンプレート一覧表示
          <div className="flex max-h-[640px] overflow-y-auto">
            <CrewTable
              tableOptions={tableOptions}
              onRowClick={handleSelectTemplate}
              showPager={false}
              showColumnSelector={false}
            />
          </div>
        )}
      </div>
    )
  }
)
